import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api';
import { map } from 'rxjs/operators';
import { GetTenantAnonymousGQL } from 'src/generated/graphql';
import { LanguageNotYetLoadedError } from './nebulr-translation.service';
import { ENABLED_LANGUAGES } from '../constans';

/**
 * The NebulrTranslationService helps UI to render different languages / translations. It wraps ngx-translate under the hood.
 * On initialization, it will query GetTenantGQL and set current language based on Tenant locale.
 * An anonymous clone `NebulrAnonymousTranslationService` (that queries GetTenantAnonymousGQL instead) is available.
 */
 @Injectable({
  providedIn: 'root',
})
export class NebulrAnonymousTranslationService {

  /**
   * Convenient way for displaying languages in a translated dropdown. Will be re translated on language change, keeping reference to the array.
   */
  public selectableLanguages: SelectItem[];

  private readonly enabledLanguages = ENABLED_LANGUAGES.map(lang => lang.key);
  private loadedLanguages: Record<string, Record<string, unknown>> = {};
  private setLanguageAsap: string;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly translateService: TranslateService,
    public readonly getTenantAnonymousGQL: GetTenantAnonymousGQL,
    ) {

    // Register the default lang as the lang currently in use!
    this.translateService.use(this.translateService.defaultLang);

    // Download all enabled languages as JSONS via HTTP. If we had race condition with getTenantGQL, we will retry to setLanguage() again after download
    this.enabledLanguages.forEach(lang => {
      this.httpClient.get(`./assets/i18n/${lang}.json`).subscribe(translations => {
        this.loadedLanguages = {...this.loadedLanguages, [lang]: translations as Record<string, unknown>};
        if (this.setLanguageAsap && this.setLanguageAsap === lang) {
          console.log("Did set lang after http fetched it");
          this.setLanguage(this.setLanguageAsap);
        }
      })
    });

    // Pull current tenant and try to setLanguage() using the workspace locale
    this.getTenantAnonymousGQL.watch({}).valueChanges.pipe(map((result) => result.data.getTenantAnonymous)).subscribe(t => { 
      if (this.translateService.currentLang !== t.locale) {
        this.setLanguage(t.locale);
      }
    });

    // Construct a translated easy to use array of language options for UI display. Retranslate it if current language is changed
    this.selectableLanguages = this.constructSelectableLanguages(this.enabledLanguages);
    this.translateService.onLangChange.subscribe(() => {
      this.constructSelectableLanguages(this.enabledLanguages).forEach((newItem) => {
        const match = this.selectableLanguages.find(currentItem => currentItem.value === newItem.value);
        match.label = newItem.label;
      });
    });
  }

  /**
   * Sets the current language via ngx-translate.
   * @param language 
   * @returns 
   */
  setLanguage(language: string): Promise<void> {
    try {
      if (!this.translateService.getLangs().includes(language)) {
        this.translateService.setTranslation(language, this.getTranslations(language), true);
      }
      this.translateService.use(language).subscribe();
      return null;
    } catch (error) {
      if (error instanceof LanguageNotYetLoadedError){
        // Tell http observers to set this language asap
        this.setLanguageAsap = language;
      } else {
        throw error;
      }
    }
  }

  /**
   * Convenient way for displaying languages in a translated dropdown
   * @returns 
   */
  constructSelectableLanguages(selectableLanguages: string[]): SelectItem[] {
    return selectableLanguages.map(lang => {return {value: lang, label: this.translateService.instant(`LANGUAGES.${lang.toUpperCase()}`)}});
  }

  private getTranslations(language: string): Record<string, unknown> {
    if (this.enabledLanguages.includes(language) && !{}.hasOwnProperty.call(this.loadedLanguages, language))
      throw new LanguageNotYetLoadedError(`${language} not loaded yet`);

    return this.loadedLanguages[language];
  }
}